
import React, { useState } from 'react';
import { useMasterLicense } from '@/hooks/useMasterLicense';
import MasterLicenseLayout from '@/components/masterlicense/MasterLicenseLayout';
import TremendousGiftCardCatalog from '@/components/masterlicense/tremendous/TremendousGiftCardCatalog';
import TremendousGiftCardSelection from '@/components/masterlicense/tremendous/TremendousGiftCardSelection';
import TremendousRewardSalesReport from '@/components/masterlicense/tremendous/TremendousRewardSalesReport';
import CatalogsList from '@/components/masterlicense/tremendous/catalog/CatalogsList';
import AdminWallet from '@/components/masterlicense/wallet/AdminWallet';
import MasterWalletManager from '@/components/masterlicense/wallet/MasterWalletManager';
import RewardInvoices from '@/components/masterlicense/wallet/RewardInvoices';
import WalletTransferDialog from '@/components/masterlicense/wallet/WalletTransferDialog';
import WalletRecentCharges from '@/components/masterlicense/wallet/WalletRecentCharges';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { ShoppingCart, Wallet, CreditCard, ArrowRightLeft, RefreshCw, BriefcaseBusiness, BarChart } from 'lucide-react';
import { clearUserSession } from '@/utils/sessionManager';
import { useToast } from '@/hooks/use-toast';

const MasterLicenseRewardSales = () => {
  const { isLoading: loadingUser, user } = useMasterLicense();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('wallet-manager');
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const handleRefreshWallet = (balance: number) => {
    setCurrentBalance(balance);
  };

  const handleLogout = async () => {
    try {
      setIsLoading(true);
      console.log("Master Admin logout initiated");
      
      // Clear all user session data
      clearUserSession();
      
      toast({
        title: "Logged out",
        description: "You have been successfully logged out."
      });
      
      // Navigate to login page after logout
      window.location.href = '/login';
    } catch (error) {
      console.error('Error during logout:', error);
      toast({
        title: "Error",
        description: "Failed to log out. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MasterLicenseLayout
      isLoading={isLoading || loadingUser}
      onLogout={handleLogout}
      userRole={user?.role || 'Master Admin'}
      activeTab="reward-sales"
    >
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <h1 className="text-2xl font-bold">Reward Sales Management</h1>
          {activeTab === 'wallet' && (
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setTransferDialogOpen(true)}
                className="flex items-center"
              >
                <ArrowRightLeft className="mr-2 h-4 w-4" />
                Transfer Funds
              </Button>
            </div>
          )}
        </div>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList>
            <TabsTrigger value="wallet-manager">
              <BriefcaseBusiness className="mr-2 h-4 w-4" />
              Wallet Manager
            </TabsTrigger>
            <TabsTrigger value="wallet">
              <Wallet className="mr-2 h-4 w-4" />
              My Wallet
            </TabsTrigger>
            <TabsTrigger value="catalog">
              <ShoppingCart className="mr-2 h-4 w-4" />
              Gift Card Catalog
            </TabsTrigger>
            <TabsTrigger value="selections">
              <ShoppingCart className="mr-2 h-4 w-4" />
              Selected Cards
            </TabsTrigger>
            <TabsTrigger value="catalogs">
              <CreditCard className="mr-2 h-4 w-4" />
              Card Catalogs
            </TabsTrigger>
            <TabsTrigger value="report">
              <BarChart className="mr-2 h-4 w-4" />
              Sales Reports
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="wallet-manager">
            <MasterWalletManager />
          </TabsContent>
          
          <TabsContent value="wallet">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <AdminWallet 
                adminId={user?.id || ""} 
                onBalanceUpdate={handleRefreshWallet}
              />
              <WalletRecentCharges adminId={user?.id || ""} />
            </div>
            <RewardInvoices adminId={user?.id || ""} />
          </TabsContent>
          
          <TabsContent value="catalog">
            <TremendousGiftCardCatalog />
          </TabsContent>
          
          <TabsContent value="selections">
            <TremendousGiftCardSelection />
          </TabsContent>
          
          <TabsContent value="catalogs">
            <CatalogsList />
          </TabsContent>
          
          <TabsContent value="report">
            <TremendousRewardSalesReport />
          </TabsContent>
        </Tabs>
        
        {transferDialogOpen && (
          <WalletTransferDialog
            open={transferDialogOpen}
            onClose={() => setTransferDialogOpen(false)}
            onSuccess={() => {/* Handle success */}}
            currentBalance={currentBalance}
            transferToId="super-admin-id"
            transferToName="Super Admin Account"
          />
        )}
      </div>
    </MasterLicenseLayout>
  );
};

export default MasterLicenseRewardSales;
